import { useState, useEffect, useCallback, useRef } from 'react';
import './HeaderAvatarMenu.css';

/**
 * Compact avatar/initials button shown in the header on phone (and tablet).
 * Tapping it opens a small menu sheet with Account / Settings / Logout.
 *
 * Why this exists: the desktop header has the user's email + a Logout
 * button visible at all times. On mobile we previously dropped both,
 * leaving phone users with no visible way to sign out (Account → Logout
 * was the only path, which is two taps and one full-page navigation).
 *
 * The menu is intentionally small and bottom-right anchored so it sits
 * inside the safe-area on iPhones with home indicators.
 */
function HeaderAvatarMenu({ userInfo, onLogout, onPageChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const initials = (userInfo?.name || userInfo?.email || 'U')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('') || 'U';

  // Close on outside tap.
  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('touchstart', onDocPointer);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('touchstart', onDocPointer);
    };
  }, [open]);

  const go = useCallback((page) => {
    setOpen(false);
    if (onPageChange) onPageChange(page);
  }, [onPageChange]);

  const handleLogout = useCallback(() => {
    setOpen(false);
    if (onLogout) onLogout();
  }, [onLogout]);

  return (
    <div className="header-avatar-wrap" ref={ref}>
      <button
        className={`header-avatar-btn ${open ? 'open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        aria-expanded={open}
      >
        {initials}
      </button>

      {open && (
        <div className="header-avatar-menu" role="menu">
          <div className="header-avatar-menu-id">
            <div className="header-avatar-menu-name">{userInfo?.name || 'Signed in'}</div>
            {userInfo?.email && (
              <div className="header-avatar-menu-email">{userInfo.email}</div>
            )}
          </div>
          <button className="header-avatar-menu-item" onClick={() => go('account')}>
            Account
          </button>
          <button className="header-avatar-menu-item" onClick={() => go('settings')}>
            Settings
          </button>
          <button className="header-avatar-menu-item header-avatar-menu-item-danger" onClick={handleLogout}>
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

export default HeaderAvatarMenu;
